


















































import ScrollPosition from '@/types/components/ScrollPosition.interface'
import IListIcon, { IListBadge, IListIconType } from '@/types/components/IListIcon.interface'
import IListTile from '@/types/components/IListTile.interface'
import Person from '@/types/Person.interface'
import Vue from 'vue'
import { mapGetters } from 'vuex'
import Group from '@/types/Group.interface'
import Message from '@/types/Message.interface'
import TriPanelTools from '@/components/TriPanelTools.vue'
import Button from '@/types/components/Button.interface'
import { debounce } from 'debounce'
import Analytics from '@/services/Analytics'
export default Vue.extend({
    components: { TriPanelTools },
    data() {
        return {
            //  The IList placeholder shown when no search has been made 
            initialSearchPlaceholder: {
                content: `Enter some text into the searchbar, then press 'enter' to see your results here.`,
                img: 'startTyping'
            },
            //  Next three placeholders are shown when no item is found that the user searched for
            noGroupsFoundPlaceholder: {
                content: `No groups found, please try adjusting search`,
                img: 'noSearch'
            },
            noMessagesFoundPlaceholder: {
                content: `No messages found within the last 30 days`,
                button: {
                    text: 'Search All Time',
                    callback: () => {this.$router.push('/messages')},
                },
                img: 'noSearch'
            },
            noMessagesFoundPlaceholderMobile: {
                content: `No messages found within the last 30 days`,
                img: 'noSearch'
            },
            noPersonsFoundPlaceholder: {
                content: `No contacts found, please try adjusting search`,
                img: 'noSearch'
            },
            toolbar: {
                title: 'Search',
                left: [
                    {
                        title: 'Back',
                        icon: 'mdi-chevron-left',
                        back: '/home',
                        tooltip: 'Back'
                    }
                ],
                right: []
            },
            //  Variables hold which search is currently active with the default being the persons (contacts) search
            searchPersons: true,
            searchGroups: false,
            searchMessages: false,
        }
    },
    mounted() {
        this.onClear()
    },
    computed: {
        ...mapGetters({
            isApp: 'isApp',
            scrollPositionLeft: 'scrollPositionLeft',
            searchBarText: 'searchBarText',
            searchBarListNextPage: 'searchBarListNextPage',
            searchBarListLastPage: 'searchBarListLastPage',
            personItems: 'personsSearchList',
            groupItems: 'groupsSearchList',
            messageItems: 'messagesSearchList',
            tenant: 'tenant',
            userHasPermission: 'userHasPermission'
        }),
        //  Our tools we display on the top toolbar for managing contacts when NOT multiselected
        contactActions(): Button[] {
            return [
                {
                    text: 'Add Contact',
                    icon: 'mdi-account-plus',
                    color: 'primary',
                    callback: () => this.popNewContact()
                },
                {
                    text: 'Import Contacts',
                    icon: 'mdi-arrow-expand-up',
                    color: 'primary',
                    disabledPopup: !this.$store.getters.userHasPermission(2),
                    disabledPopupText:
                        "You don't have permission to access this feature. Please contact your account's admin to perform this task or have them update your account permission",
                    hidden: this.isQBOXeroUser() || this.isApp || this.isMobile,
                    callback: () => this.$router.push('/csv/import')
                }
            ]
        },
        //  The IList pagination params
        params(): object {
            return {
                limit: 30,
                searchtext: this.searchBarText.replace(/[()]/g, ''),
                paginate: true,
                page: 1
            }
        },
        //  Holds the users current search query
        searchBarText: {
            get(): string {
                return this.$store.getters.searchBarText
            },
            set: debounce(function(value: string): void {
                this.$store.commit('searchBarTextSet', value)
                this.searchDeep()
            }, 250)
        },
        //  Depending on the search the user is doing we change the placeholder to help tell the user what they are searching for
        searchBarPlaceHolder() {
            if (this.searchPersons) return "Search for Contacts"

            if (this.searchGroups) return "Search for Groups"

            if (this.searchMessages) return "Search for Messages"
        }
    },
    methods: {
        //  Callback function used in our IList for the groups search which takes user to the group of the tile they click
        goToGroup(group: Group): void {
            this.$router.push(`/groups/${group.id}?sender=${this.$route.path}`)
        },
        //  Callback function used in our IList for the persons search which takes user to the channel of the tile they click
        goToPerson(person: Person): void {
            this.$router.push(`/inbox?channel=${person.primary_channel_id}`)
        },
        goToMessage(message: Message): void {
            //  If the user clicked on a scheduled message we add an additional search parameter so we can modify the SmsMessagesPanel filter to show scheduled messages
            //  since they are not shown in the default filter, the message id is also passed so it can be scrolled to
            if (message.type === 'scheduled') return this.$router.push(`/inbox?channel=${message.channel_id}&item=${message.id}&filter=${message.type}`)
            
            //  Otherwise just pass the message id so we can scroll to it when the channel is loaded
            this.$router.push(`/inbox?channel=${message.channel_id}&item=${message.id}`)
        },
        //  The IListIcon bound to the persons list
        groupIcon(group: Group): IListIcon {
            //  Get our icon from the vue filter
            const icon = this.$options.filters.groupIcon(group)

            //  Get our badge icon
            const badgeIcon = this.$options.filters.groupBadge(group)

            //  Get our badge
            const badge: IListBadge = {
                color: 'grey darken-1',
                icon: badgeIcon
            }

            //  Create our IListIcon object
            const listIcon: IListIcon = {
                type: IListIconType.icon,
                content: icon,
                color: 'grey darken-1'
            }

            //  If we have a badge icon, add badge to our ilisticon
            if (badgeIcon) listIcon.badge = badge

            //  Return our IListIcon object
            return listIcon
        },
        //  The group tile bound to the groups list
        groupTile(): IListTile {
            //  We use a temp variable and don't change the actual search text the user sees, reason is if the user decides to format their search like so (775)
            //  if we change this.searchBarText then what they type will get replaced and causes a bad UX
            const searchText  = this.searchBarText.replace(/[()]/g, '')
            return {
                leftFooter: (group: Group) => this.$options.filters.groupType(group),
                leftIcon: (group: Group) => this.groupIcon(group),
                line1: (group: Group) => {
                    const str = group.name
                    const highlight = this.$options.filters.highlight(str, searchText)
                    return highlight
                },
                line2: (group: Group) => {
                    const str = group.description
                    const highlight = this.$options.filters.highlight(str, searchText)
                    return highlight
                },
                line3: (group: Group) => {
                    const str = this.$options.filters.groupPersonsCount(group)
                    const highlight = this.$options.filters.highlight(str, searchText)
                    return highlight
                },
                    
                callback: (group: Group) => this.goToGroup(group)
            }
        },
        isQBOXeroUser(): boolean {
            //  Grab the tenant's data provider
            const provider = this.tenant.data_provider

            //  If provider is null, return false
            if (!provider) return false

            //  Else, check if they are QBO or Xero
            const isQBOXeroUser =
                provider.toLowerCase() === 'qbo' || provider.toLowerCase() === 'xero'

            //  Return true if they are QBO or Xero
            return isQBOXeroUser
        },
        //  The IListIcon bound to the messages list
        messageIcon(message: Message): IListIcon {
            //  Get our icon from the vue filter
            const icon = this.$options.filters.messageIcon(message)

            //  Create our IListIcon object
            const listIcon: IListIcon = {
                type: IListIconType.icon,
                content: icon,
                color: 'grey darken-1'
            }

            //  Scheduled messages only include a clock badge to indicate they are scheduled 
            if (message.type === 'scheduled') {
                //  Create our badge
                const badge: IListBadge = {
                    color: 'grey darken-1',
                    icon: 'mdi-clock'
                }

                listIcon.badge = badge

                return listIcon
            }

            //  Return our IListIcon object
            return listIcon
        },
        //  The message tile bound to the messages list
        messageTile(): IListTile {
            //  We use a temp variable and don't change the actual search text the user sees, reason is if the user decides to format their search like so (775)
            //  if we change this.searchBarText then what they type will get replaced and causes a bad UX
            const searchText  = this.searchBarText.replace(/[()]/g, '')
            return {
                leftFooter: (message: Message) => this.$options.filters.messageType(message),
                leftIcon: (message: Message) => this.messageIcon(message),
                line1: (message: Message) => {
                    const str = message.text
                    const highlight = this.$options.filters.highlight(str, searchText)
                    return highlight
                },
                line2: (message: Message) => {
                    const str = "From: " + this.$options.filters.messageAuthor(message)
                    const highlight = this.$options.filters.highlight(str, searchText)
                    return highlight
                },
                line3: (message: Message) => {
                    const str = "To: " + this.$options.filters.messageTo(message)
                    const highlight = this.$options.filters.highlight(str, searchText)
                    return highlight
                },
                line4: (message: Message) => {
                    const str =  "Message Status: " + message.status
                    const highlight = this.$options.filters.highlight(str, searchText)
                    return highlight
                },
                    
                callback: (message: Message) => this.goToMessage(message)
            }
        },
        navigateToImport(): void {
            //  Take user to contat import workflow
            this.$router.push('/csv/import')
        },
        //  If the user chooses to clear the search via the x icon then we reset the search
        onClear(): void {
            //  Prevents bug where a user leaves the search experience and returns to have their search still there
            this.$store.commit('searchBarReset')
        },
        //  Paginate the next page of the current filtered list
        paginateCurrentList(): void {
            //  Get current pagination params
            const params = this.params

            if (this.searchBarListNextPage > this.searchBarListLastPage) return

            params.page = this.searchBarListNextPage

            //  Paginate the next page
            this.searchDeep()
        },
        //  The IListIcon bound to the persons list
        personIcon(person: Person): IListIcon {
            //  Get our icon from the vue filter
            const icon = this.$options.filters.personIcon(person)

            //  Create our IListIcon object
            const listIcon: IListIcon = {
                type: IListIconType.icon,
                content: icon,
                color: 'grey darken-1'
            }

            //  Return our IListIcon object
            return listIcon
        },
        //  The persons tile bound to the persons list
        personTile(): IListTile {
            //  We use a temp variable and don't change the actual search text the user sees, reason is if the user decides to format their search like so (775)
            //  if we change this.searchBarText then what they type will get replaced and causes a bad UX
            const searchText  = this.searchBarText.replace(/[()]/g, '')
            return {
                leftFooter: (person: Person) => this.$options.filters.personType(person),
                leftIcon: (person: Person) => this.personIcon(person),
                line1: (person: Person) => {
                    //  We format the line, in this case the persons phone number, and then apply a highlight for the matching text which is shown to the user if there is a match
                    const str = this.$options.filters.personFullNameOrEmail(person)
                    const highlight = this.$options.filters.highlight(str, searchText)
                    return highlight
                },
                line2: (person: Person) => {
                    const str = this.$options.filters.personDefaultPhone(person)
                    const highlight = this.$options.filters.highlight(str, searchText)
                    return highlight
                },
                line3: (person: Person) => {
                    const str = this.$options.filters.addressString(person.address_billing)
                    const highlight = this.$options.filters.highlight(str, searchText)
                    return highlight
                },
                line4: (person: Person) => {
                    const str =  this.$options.filters.extensionsToString(person.extensions)
                    const highlight = this.$options.filters.highlight(str, searchText)
                    return highlight
                },
                    
                callback: (person: Person) => this.goToPerson(person)
            }
        },
        //  Pop a new contact form
        popNewContact(): void {
            //  Our base new person model
            const newPerson = {
                name_given: null,
                name_family: null,
                phone_default: {
                    free_form_number: null,
                    default: true,
                    type: 'MOBILE'
                }
            }

            //  Open the dialog app workflow to create a new contact
            this.$store.dispatch('dialogOpen', {
                type: 'APP',
                title: 'Add Contact',
                icon: 'mdi-account',
                color: 'primary',
                component: 'new-contact-selector',
                props: {
                    model: newPerson,
                    withImport: true
                },
                callback: async (person: Person) => {
                    //  If the request failed, do nothing
                    if (!person) return null

                    //  Else, add the new contact to the top of the personsList
                    this.selectedPerson = person

                    //  close the app dialog
                    this.$store.dispatch('dialogClose', 'APP')
                }
            })
        },
        searchDeep(): void {
            //  If the user gets rid of the searchtext via backspaces we want to reset the search once they get rid of all the text so we don't
            //  cache search items and show them when no search is occuring
            if (!this.searchBarText) {
                return this.onClear()
            }

            //  Remove parentheses from our search text because it turns our search query into a regex
            const text  = this.searchBarText.replace(/[()]/g, '')

            //  Validate the search text for length and whitespace
            const isValid = text && text.length > 1 && !text.isNullOrWhiteSpace()

            //  If the search text is NOT valid, do nothing
            if (!isValid) return

            //  If we are in the contact search section we will pass the search to our persons search, same applies for messages and groups
            if (this.searchPersons) {
                Analytics.track('GlobalSearchUsed', {
                    search_type: 'Contacts',
                    search_query: text
                })

               return this.searchForPersons()
            }

            if (this.searchGroups) {
                Analytics.track('GlobalSearchUsed', {
                    search_type: 'Groups',
                    search_query: text
                })

               return this.searchForGroups()
            }

            if (this.searchMessages) {
                Analytics.track('GlobalSearchUsed', {
                    search_type: 'Messages',
                    search_query: text
                })

                return this.searchForMessages()
            }

        },
        async searchForGroups(): Promise<void> {
            //  Send the search request
            const page = await this.$store.dispatch('groupsSearch', this.params)

            //  If the request failed, do nothing
            if (!page) return
        },
        async searchForPersons(): Promise<void> {
            //  Send the search request
            const page = await this.$store.dispatch('personsSearch', this.params)

            //  If the request failed, do nothing
            if (!page) return
        },
        async searchForMessages(): Promise<void> {
            //  Send the search request
            const page = await this.$store.dispatch('messagesSearch', this.params)

            //  If the request failed, do nothing
            if (!page) return
        },
    },
    watch: {
        scrollPositionLeft(position: ScrollPosition): void {
            //  If we are NOT paginating AND scroll is at bottom, paginate next page
            if (position.bottom) this.paginateCurrentList()
        }
    }
})
